import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowRight } from "lucide-react";

const CoverImg = ({ src, alt, className }) =>
  src ? (
    <img
      src={mediaUrl(src)}
      alt={alt}
      loading="lazy"
      className={className}
    />
  ) : (
    <div className={`${className} bg-[#E7E5E4] flex items-center justify-center`}>
      <span className="font-display italic text-3xl text-[#A8A29E]">Pensieri</span>
    </div>
  );

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hubs, setHubs] = useState([]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/posts")
      .then((res) => setPosts(res.data))
      .finally(() => setLoading(false));
    api
      .get("/categories/hubs")
      .then((res) => setHubs(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHubs([]));
  }, []);

  const hero = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="relative z-10">
      <Header />

      {/* Intro / hero heading */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#1C1917] max-w-4xl fade-up">
          Storie, idee e <span className="italic text-[#9F583F]">curiosità</span> per guardare il mondo con occhi diversi.
        </h1>
        <p className="font-body text-lg text-[#57534E] mt-8 max-w-2xl leading-relaxed fade-up">
          Uno spazio personale dove condivido le cose che mi appassionano. Leggi,
          lascia un commento e, se ti va, condividi.
        </p>

        {hubs.length > 0 && (
          <div className="mt-10 fade-up" data-testid="home-hubs">
            <p className="label-ui text-[#A8A29E] mb-4">Raccolte tematiche</p>
            <div className="flex flex-wrap gap-2.5">
              {hubs.map((h) => (
                <Link
                  key={h.slug}
                  to={`/categoria/${h.slug}`}
                  data-testid={`home-hub-${h.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-[#E7E5E4] bg-white px-4 py-2 label-ui text-[#57534E] transition-colors duration-300 hover:border-[#9F583F] hover:text-[#9F583F]"
                >
                  {h.name}
                  <ArrowRight size={13} strokeWidth={1.6} className="opacity-0 -ml-1.5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Feed */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-10" data-testid="post-feed">
        {loading ? (
          <p className="font-ui text-sm text-[#57534E] py-20">Caricamento articoli…</p>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center border-t border-[#E7E5E4]">
            <p className="font-display text-3xl text-[#1C1917] italic">
              Ancora nessun articolo pubblicato.
            </p>
            <p className="font-body text-[#57534E] mt-3">
              Torna presto per leggere i primi pensieri.
            </p>
          </div>
        ) : (
          <>
            {/* Hero article */}
            {hero && (
              <Link
                to={`/articolo/${hero.slug}`}
                data-testid="hero-post"
                className="group grid md:grid-cols-2 gap-8 md:gap-12 items-center border-t border-[#E7E5E4] pt-10 mb-16"
              >
                <div className="overflow-hidden relative">
                  <CoverImg
                    src={hero.cover_image}
                    alt={hero.title}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <span className="pointer-events-none absolute inset-0 bg-[#1C1917] opacity-0 group-hover:opacity-[0.06] transition-opacity duration-300" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="label-ui text-[#9F583F]">In evidenza</span>
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl leading-[1] tracking-tight text-[#1C1917] group-hover:text-[#9F583F] transition-colors">
                    {hero.title}
                  </h2>
                  <p className="font-body text-lg text-[#57534E] mt-5 leading-relaxed line-clamp-3">
                    {hero.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-6 label-ui text-[#1C1917] group-hover:text-[#9F583F] group-hover:gap-3 transition-all duration-300">
                    Leggi l'articolo <ArrowRight size={15} strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            )}

            {/* Grid of the rest */}
            {rest.length > 0 && (
              <div className="grid md:grid-cols-3 gap-x-8 gap-y-14 border-t border-[#E7E5E4] pt-14">
                {rest.map((p, i) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.09, ease: "easeOut" }}
                  >
                    <Link
                      to={`/articolo/${p.slug}`}
                      data-testid={`post-card-${p.slug}`}
                      className="group flex flex-col transition-transform duration-300 ease-out hover:-translate-y-1.5"
                    >
                      <div className="overflow-hidden mb-5 relative">
                        <CoverImg
                          src={p.cover_image}
                          alt={p.title}
                          className="w-full aspect-[3/2] object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                        />
                        <span className="pointer-events-none absolute inset-0 bg-[#1C1917] opacity-0 group-hover:opacity-[0.06] transition-opacity duration-300" />
                      </div>
                      <h3 className="font-display text-2xl md:text-[1.7rem] leading-tight tracking-tight text-[#1C1917] group-hover:text-[#9F583F] transition-colors">
                        {p.title}
                      </h3>
                      <p className="font-body text-[#57534E] mt-3 leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-2 mt-4 label-ui text-[#9F583F] opacity-0 -translate-x-1.5 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                        Leggi l'articolo <ArrowRight size={14} strokeWidth={1.6} />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}
